import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PAGES, canAccess } from "@/lib/pages";
import { InstallButton } from "@/components/InstallPrompt";
import {
  House, Factory, Package, Truck, Tag, Users, Cube,
  Scissors, Needle, ClockCounterClockwise, SignOut, Receipt, UsersThree, Archive, DeviceMobile, List, X
} from "@phosphor-icons/react";

const ICONS = {
  dashboard: House,
  parties: Users,
  products: Tag,
  yarn: Package,
  knitting: Factory,
  dyeing: Factory,
  compacting: Factory,
  cutting: Scissors,
  stitching: Needle,
  packing: Archive,
  stock: Cube,
  dispatch: Truck,
  accessories: ClockCounterClockwise,
  ledger: Receipt,
  users: UsersThree,
};

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);          // desktop: collapse to icons only
  const [mobileOpen, setMobileOpen] = useState(false);        // mobile: drawer open

  // Close mobile drawer whenever the route changes
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const visible = PAGES.filter((p) => canAccess(user, p.key));
  const isAdmin = user?.role === "admin";
  const showLabels = mobileOpen || !collapsed; // on mobile drawer always shows labels

  const sidebarWidth = mobileOpen ? "w-64" : collapsed ? "w-16" : "w-64";

  return (
    <div className="min-h-screen flex bg-background">
      {/* MOBILE TOP BAR */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-white border-b border-border flex items-center justify-between px-3">
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-toggle"
          className="w-10 h-10 border border-border flex items-center justify-center hover:bg-secondary"
          aria-label="Open menu"
        >
          <List size={18} weight="bold" />
        </button>
        <div className="text-center leading-none">
          <div className="label-caps">Selvalakshmi</div>
          <div className="font-display font-black text-base leading-none mt-0.5 tracking-tight">SLKC</div>
        </div>
        <div className="flex items-center gap-2">
          <InstallButton testid="install-button-mobile" />
          <button
            onClick={async () => { await logout(); nav("/login"); }}
            data-testid="mobile-logout"
            className="w-10 h-10 border border-border flex items-center justify-center hover:bg-secondary"
            aria-label="Logout"
          >
            <SignOut size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* MOBILE OVERLAY */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-foreground/40 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* SIDEBAR */}
      <aside
        className={`${sidebarWidth} border-r border-border bg-white shrink-0 h-screen flex flex-col
          ${mobileOpen ? "fixed inset-y-0 left-0 z-50" : "hidden md:flex md:sticky md:top-0"}
          transition-[width] duration-200`}
      >
        <div className="h-14 border-b border-border flex items-center px-4 shrink-0">
          {/* Desktop collapse toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-toggle"
            className="hidden md:flex w-8 h-8 border border-border items-center justify-center font-mono text-xs hover:bg-secondary"
          >
            {collapsed ? "▶" : "◀"}
          </button>
          {/* Mobile close */}
          <button
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-menu-close"
            className="md:hidden w-8 h-8 border border-border flex items-center justify-center hover:bg-secondary"
            aria-label="Close menu"
          >
            <X size={14} weight="bold" />
          </button>
          {showLabels && (
            <div className="ml-3">
              <div className="label-caps">Selvalakshmi</div>
              <div className="font-display font-black text-lg leading-none mt-0.5 tracking-tight">SLKC</div>
            </div>
          )}
        </div>

        <nav className="p-2 flex-1 overflow-y-auto">
          {visible.map((n) => {
            const Icon = ICONS[n.key] || House;
            return (
              <NavLink
                key={n.key}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.key}`}
                className={({ isActive }) =>
                  `nav-item flex items-center gap-3 px-3 py-2 mb-0.5 border border-transparent ${isActive ? "active" : ""}`
                }
              >
                <Icon size={16} weight="bold" />
                {showLabels && <span className="text-[13px]">{n.label}</span>}
              </NavLink>
            );
          })}
          {isAdmin && (
            <>
              <NavLink
                to="/users"
                data-testid="nav-users"
                className={({ isActive }) =>
                  `nav-item flex items-center gap-3 px-3 py-2 mb-0.5 border border-transparent mt-2 border-t border-border rounded-none ${isActive ? "active" : ""}`
                }
              >
                <UsersThree size={16} weight="bold" />
                {showLabels && <span className="text-[13px]">Users & Access</span>}
              </NavLink>
              <NavLink
                to="/workers"
                data-testid="nav-workers"
                className={({ isActive }) =>
                  `nav-item flex items-center gap-3 px-3 py-2 mb-0.5 border border-transparent ${isActive ? "active" : ""}`
                }
              >
                <Users size={16} weight="bold" />
                {showLabels && <span className="text-[13px]">Workers</span>}
              </NavLink>
            </>
          )}
          {(canAccess(user, "cutting") || canAccess(user, "packing")) && (
            <NavLink
              to="/ops"
              data-testid="nav-ops"
              className="nav-item flex items-center gap-3 px-3 py-2 mb-0.5 border border-transparent bg-secondary hover:bg-foreground hover:text-background"
            >
              <DeviceMobile size={16} weight="bold" />
              {showLabels && <span className="text-[13px]">Shop Floor Ops</span>}
            </NavLink>
          )}
        </nav>

        <div className="border-t border-border p-3 shrink-0 space-y-2">
          {showLabels && <InstallButton testid="install-button-sidebar" className="w-full justify-center" />}
          {showLabels ? (
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-xs font-semibold truncate">{user?.name}</div>
                <div className="label-caps">{user?.role}</div>
              </div>
              <button
                onClick={async () => { await logout(); nav("/login"); }}
                data-testid="logout-button"
                className="w-8 h-8 border border-border flex items-center justify-center hover:bg-secondary shrink-0"
                title="Logout"
              >
                <SignOut size={14} weight="bold" />
              </button>
            </div>
          ) : (
            <button
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="logout-button-mini"
              className="w-8 h-8 border border-border flex items-center justify-center hover:bg-secondary mx-auto"
            >
              <SignOut size={14} weight="bold" />
            </button>
          )}
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 min-w-0 pt-14 md:pt-0 pb-16 md:pb-0">
        <Outlet />
      </main>

      {/* MOBILE BOTTOM NAV — Dashboard · Cutting · Packing · Dispatch */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-border grid grid-cols-4">
        {[
          { key: "dashboard", to: "/", label: "Home", icon: House },
          { key: "cutting", to: "/cutting", label: "Cutting", icon: Scissors },
          { key: "packing", to: "/packing", label: "Packing", icon: Archive },
          { key: "dispatch", to: "/dispatch", label: "Dispatch", icon: Truck },
        ]
          .filter((n) => canAccess(user, n.key))
          .map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.key}
                to={n.to}
                end={n.to === "/"}
                data-testid={`bottom-nav-${n.key}`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-0.5 py-2 border-t-2 ${
                    isActive
                      ? "border-foreground text-foreground bg-secondary"
                      : "border-transparent text-muted-foreground"
                  }`
                }
              >
                <Icon size={20} weight="bold" />
                <span className="text-[10px] font-mono uppercase tracking-widest">{n.label}</span>
              </NavLink>
            );
          })}
      </nav>
    </div>
  );
}
